import { useEffect, useRef } from 'react'
import svg from './assets/logo_anim.svg?raw'

// Animația logo-ului din trailer (vinewood_v2/tools/logo/anim: build.py + anim.js), pe straturi:
// cercul apare din centru, soarele răsare, palmierii cresc, literele aterizează pe HIT, apoi blit + undă + luciu.
// SVG-ul e generat de build.py din logo.svg, cu id-urile prefixate „la-” ca să nu se bată cu pagina.
const DUR = 4.0
const HIT = 1.0
const DELAY = 0.25 // pauza scurtă după încărcare, înainte să înceapă
const CX = 435.47, CY = 434.19, R = 434.8

const cl = (v, a = 0, b = 1) => Math.max(a, Math.min(b, v))
const seg = (t, a, b) => cl((t - a) / (b - a))
const eOut = (x) => 1 - Math.pow(1 - x, 3)
const eIn = (x) => x * x * x
const eInOut = (x) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2)
const eBack = (x) => { const c1 = 1.70158, c3 = c1 + 1; return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2) }
const spring = (x) => (x <= 0 ? 0 : x >= 1 ? 1 : 1 - Math.exp(-6 * x) * Math.cos(9 * x))
const around = (px, py, rot, s) => `translate(${px} ${py}) rotate(${rot}) scale(${s}) translate(${-px} ${-py})`

const PALM = { palmL: { x: 150, y: 505, a: -28, t0: 0.40, ph: 0 }, palmR: { x: 770, y: 505, a: 28, t0: 0.48, ph: 1.7 } }

// frame(t) desenează cadrul de la secunda t (aceeași logică ca anim.js din trailer)
const makeFrame = (root) => {
    const $ = (id) => root.querySelector('#la-' + id)
    const letters = [...root.querySelectorAll('#la-letters .L')]
    const bb = $('letters').getBBox()
    const WORD = { x: bb.x + bb.width / 2, y: bb.y + bb.height / 2 }
    const rings = [$('ring1'), $('ring2')].map((c) => {
        const L = 2 * Math.PI * Number(c.getAttribute('r'))
        c.setAttribute('stroke-dasharray', `${L} ${L}`)
        return { c, L }
    })

    return (t) => {
        const push = 1 + 0.045 * eOut(seg(t, HIT, DUR))
        let sx = 0, sy = 0
        if (t >= HIT) { const k = Math.exp(-9 * (t - HIT)) * 9; sx = k * Math.sin(38 * (t - HIT)); sy = k * Math.cos(31 * (t - HIT)) }
        $('all').setAttribute('transform', `translate(${sx} ${sy}) ` + around(CX, CY, 0, push))

        const b = seg(t, 0, 0.55)
        $('badge').setAttribute('transform', around(CX, CY, 0, 0.55 + 0.45 * eBack(b)))
        $('badge').setAttribute('opacity', eOut(seg(t, 0, 0.22)))
        rings.forEach((r, i) => r.c.setAttribute('stroke-dashoffset', r.L * (1 - eOut(seg(t, 0.15 + i * 0.08, 0.8 + i * 0.08)))))

        const s = eOut(seg(t, 0.25, 0.85))
        $('sun').setAttribute('transform', `translate(0 ${140 * (1 - s)})`)
        $('sun').setAttribute('opacity', eOut(seg(t, 0.25, 0.5)))

        for (const id of ['palmL', 'palmR']) {
            const P = PALM[id], x = seg(t, P.t0, P.t0 + 0.6)
            const rot = P.a * (1 - spring(x)) + (t > 1.2 ? 1.3 * Math.sin(2 * Math.PI * (t - 1.2) / 3.2 + P.ph) * seg(t, 1.2, 1.8) : 0)
            $(id).setAttribute('transform', around(P.x, P.y, rot, 0.3 + 0.7 * eBack(x)))
            $(id).setAttribute('opacity', eOut(seg(t, P.t0, P.t0 + 0.15)))
        }

        const x = seg(t, HIT - 0.14, HIT)
        let sc = 1.45 - 0.45 * eIn(x)
        const land = seg(t, HIT, HIT + 0.16)
        if (land > 0 && land < 1) sc = 1 - 0.035 * Math.sin(Math.PI * land)
        $('letters').setAttribute('transform', around(WORD.x, WORD.y, 0, sc))
        letters.forEach((g, i) => g.setAttribute('opacity', eOut(seg(t, HIT - 0.14 + i * 0.012, HIT - 0.10 + i * 0.012))))

        $('flash').setAttribute('opacity', t >= HIT ? 0.42 * (1 - eOut(seg(t, HIT, HIT + 0.3))) : 0)
        const w = seg(t, HIT, HIT + 0.55)
        $('wave').setAttribute('r', R + 260 * eOut(w))
        $('wave').setAttribute('stroke-width', 10 - 9 * w)
        $('wave').setAttribute('opacity', t >= HIT ? 0.7 * (1 - w) : 0)

        $('shineR').setAttribute('x', -400 + 1500 * eInOut(seg(t, HIT + 0.45, HIT + 1.15)))

        let g = 0.5 * eOut(seg(t, 0.3, HIT))
        if (t >= HIT) g = 0.6 + 0.4 * Math.exp(-5 * (t - HIT)) + 0.06 * Math.sin(2 * Math.PI * (t - HIT) / 2.4)
        $('glowC').setAttribute('opacity', cl(g))
    }
}

const LogoAnim = () => {
    const ref = useRef(null)

    useEffect(() => {
        const frame = makeFrame(ref.current)
        frame(0)
        const t0 = performance.now()
        let raf = 0
        const loop = (now) => {
            const t = (now - t0) / 1000 - DELAY
            frame(cl(t, 0, DUR))
            if (t < DUR) raf = requestAnimationFrame(loop)
        }
        raf = requestAnimationFrame(loop)
        return () => cancelAnimationFrame(raf)
    }, [])

    return <div className="logo-anim-wrap" ref={ref} dangerouslySetInnerHTML={{ __html: svg }} />
}

export default LogoAnim
